import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_GROUPFINANCE_LOAD_BY_GROUP_ID,
    ACTION_GROUPFINANCE_LOAD_BY_ID,
    ACTION_GROUPFINANCE_LOAD_FAILURE,
    ACTION_GROUPFINANCE_LOAD_SUCCESS,

    ACTION_GROUPFINANCE_SAVE,
    ACTION_GROUPFINANCE_SAVE_SUCCESS,
    ACTION_GROUPFINANCE_SAVE_FAILURE,

    ACTION_GROUPFINANCE_VALIDATE,
    ACTION_GROUPFINANCE_VALIDATE_SUCCESS,
    ACTION_GROUPFINANCE_VALIDATE_FAILURE,

    ACTION_GROUPFINANCE_CREATE,
    ACTION_GROUPFINANCE_CREATE_SUCCESS,
    ACTION_GROUPFINANCE_CREATE_FAILURE,

    ACTION_GROUPFINANCE_SYNC,
    ACTION_GROUPFINANCE_SYNC_SUCCESS,
    ACTION_GROUPFINANCE_SYNC_FAILURE,

    ACTION_GROUPFINANCE_CLEAR,
    ACTION_GROUPFINANCE_FILTER,

    GroupFinanceAction
} from '../actions/groupFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IGroupFinanceState {
    formData: Dtos.GroupFinance | Dtos.GroupFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.GroupFinance[];
    formLength: number;
}

const initialState: IGroupFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IGroupFinanceState, _isLoading: boolean): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IGroupFinanceState,
    formData: Dtos.GroupFinance | Dtos.GroupFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IGroupFinanceState, responseStatus: Dtos.ResponseStatus): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IGroupFinanceState, formFilter: IFormFilter): IGroupFinanceState {

    const items: Dtos.GroupFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.taxInvoiceName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: IGroupFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IGroupFinanceState, propertiesToClear?: string[]): IGroupFinanceState {

    if (!propertiesToClear) {
        const newState: IGroupFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IGroupFinanceState, _isSaving: boolean): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IGroupFinanceState,
    formData: Dtos.GroupFinance | Dtos.GroupFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IGroupFinanceState, responseStatus: Dtos.ResponseStatus): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IGroupFinanceState, _isValidating: boolean): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IGroupFinanceState,
    formData: Dtos.GroupFinance | Dtos.GroupFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IGroupFinanceState, responseStatus: Dtos.ResponseStatus): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IGroupFinanceState, _isCreating: boolean): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IGroupFinanceState,
    formData: Dtos.GroupFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IGroupFinanceState, responseStatus: Dtos.ResponseStatus): IGroupFinanceState {
    const newState: IGroupFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const GroupFinanceReducer: Reducer<IGroupFinanceState> = (state: IGroupFinanceState = initialState, action: GroupFinanceAction) => {
    switch (action.type) {
        case ACTION_GROUPFINANCE_LOAD_BY_GROUP_ID:
        case ACTION_GROUPFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_GROUPFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_GROUPFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_GROUPFINANCE_SAVE:
            return saving(state, true);
        case ACTION_GROUPFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_GROUPFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_GROUPFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_GROUPFINANCE_CREATE:
            return creating(state, true);
        case ACTION_GROUPFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_GROUPFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_GROUPFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_GROUPFINANCE_SYNC:
            return loading(state, true);
        case ACTION_GROUPFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default GroupFinanceReducer;